import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/** Counts from the previous value to `value` with an ease-out curve. */
export function CountUp({ value, duration = 1200, decimals = 0, prefix = '', suffix = '' }: {
  value: number; duration?: number; decimals?: number; prefix?: string; suffix?: string;
}) {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    // Reduced motion: show the final value at once.
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(value);
      from.current = value;
      return undefined;
    }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(origin + (value - origin) * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
      else from.current = value;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);
  return <>{prefix}{shown.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</>;
}

/**
 * A live sparkline: the series keeps shifting left as new points arrive, so the card always
 * shows data "moving". `seed` gives each card its own shape; `base` anchors it to a real value.
 */
export function LiveSparkline({ seed = 1, base = 10, points = 28, interval = 1400, id }: {
  seed?: number; base?: number; points?: number; interval?: number; id: string;
}) {
  const rand = useMemo(() => {
    let s = seed * 9301 + 49297;
    return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  }, [seed]);
  const next = (prev: number) => Math.max(0.5, prev + (rand() - 0.46) * Math.max(1, base * 0.18));
  const [series, setSeries] = useState<number[]>(() => {
    const out: number[] = [];
    let v = Math.max(1, base * 0.7);
    for (let i = 0; i < points; i += 1) { v = next(v); out.push(v); }
    return out;
  });
  useEffect(() => {
    const timer = window.setInterval(() => setSeries((prev) => [...prev.slice(1), next(prev[prev.length - 1])]), interval);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval]);

  const w = 300; const h = 56;
  const max = Math.max(...series); const min = Math.min(...series);
  const span = Math.max(1, max - min);
  const coords = series.map((v, i) => [(i / (series.length - 1)) * w, h - 6 - ((v - min) / span) * (h - 14)]);
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">
      <defs>
        <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--px-accent-1)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--px-accent-1)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-line`} x1="0" x2="1">
          <stop offset="0%" stopColor="var(--px-accent-3)" />
          <stop offset="100%" stopColor="var(--px-accent-2)" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id}-fill)`} style={{ transition: 'd .9s ease' }} />
      <path d={line} fill="none" stroke={`url(#${id}-line)`} strokeWidth="2.4" strokeLinecap="round" style={{ transition: 'd .9s ease' }} />
      <circle cx={lx} cy={ly} r="4" fill="var(--px-accent-2)">
        <animate attributeName="r" values="3;6;3" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/** Animated radial ring (0..1). */
export function Ring({ value, size = 92, label }: { value: number; size?: number; label?: ReactNode }) {
  const r = size / 2 - 8; const c = 2 * Math.PI * r;
  const [v, setV] = useState(0);
  useEffect(() => { const t = window.setTimeout(() => setV(Math.max(0, Math.min(1, value))), 80); return () => window.clearTimeout(t); }, [value]);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <defs>
          <linearGradient id="px-ring" x1="0" x2="1"><stop offset="0%" stopColor="var(--px-accent-1)" /><stop offset="100%" stopColor="var(--px-accent-2)" /></linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--px-line)" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="url(#px-ring)" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - v)} style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(.2,.8,.2,1)' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontWeight: 800 }}>{label}</div>
    </div>
  );
}

/** Horizontal marquee; children are duplicated so the loop is seamless. */
export function Ticker({ children, duration = 40 }: { children: ReactNode; duration?: number }) {
  return (
    <div className="px-ticker" style={{ '--px-ticker-duration': `${duration}s` } as CSSProperties}>
      <div className="px-ticker-track">{children}{children}</div>
    </div>
  );
}

/** Vertical auto-scrolling list; children are duplicated so the loop is seamless. */
export function Stream({ children, duration = 30 }: { children: ReactNode; duration?: number }) {
  return (
    <div className="px-stream" style={{ '--px-stream-duration': `${duration}s` } as CSSProperties}>
      <div className="px-stream-track">{children}{children}</div>
    </div>
  );
}

export function Ambient() {
  return (
    <div className="px-ambient" aria-hidden="true">
      <div className="px-grid" />
      <span className="px-orb o1" /><span className="px-orb o2" /><span className="px-orb o3" />
    </div>
  );
}

/** Live clock, updated every second. */
export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), intervalMs); return () => window.clearInterval(t); }, [intervalMs]);
  return now;
}

export const COMMUNITY_ORGANIZATION_CODE = 'COMMUNITY';
